/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';
import { OVERLAY_TIMEOUT } from '../../config';
import { InteractiveComponents } from '../../core/enums/InteractiveComponents';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../interactivelayer/interactiveLayer.reducers';
import { setScreenTaskController } from '../screens/screens.reducers';
import {
    setOverlayHighlightHorizontalPositionAction,
    setOverlayHighlightVerticalLimitAction,
    setOverlayHighlightVerticalPositionAction,
} from '../overlay/overlayhighlight/overlayhighlight.actions';
import {
    overlayDisableGhostMode,
    overlayEnableGhostMode,
} from '../overlay/overlayhighlight/overlayhighlight.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import {
    overlayDialogSetIsCaretVisibleAction,
    setOverlayDialogStateAction,
    setOverlayDialogTimerIdAction,
} from './overlaydialog.actions';
import {
    OVERLAY_DIALOG_SET_IS_CARET_VISIBLE,
    SET_OVERLAY_DIALOG_STATE,
    SET_OVERLAY_DIALOG_TIMER_ID,
} from './overlaydialog.types';
import { IOverlayDialog } from './overlaydialogprops';
import { Log } from '../../core/logging';

const TAG: string = 'overlaydialog';

const defaultUIProperties: IOverlayDialog = {
    description: '',
    enableCaret: false,
    errorPriority: 0,
    hasDimmer: true,
    hasSeperator: false,
    iconURL: '',
    isPersistent: false,
    messageCode: '',
    mouseTrapValue: '',
    options: [],
    overlayType: OverlayTypes.VOID,
    subtitle: '',
    title: '',
    verticalPositionCaretAvailable: [],
    onAutoHide: () => null,
};

const initialState: {
    isCaretVisible: boolean;
    isOverlayDialogActive: boolean;
    timerId: any;
    uiProperties: IOverlayDialog;
} = {
    isCaretVisible: false,
    isOverlayDialogActive: false,
    timerId: null,
    uiProperties: defaultUIProperties,
};

const overlayDialogReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case OVERLAY_DIALOG_SET_IS_CARET_VISIBLE:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case SET_OVERLAY_DIALOG_STATE:
            return {
                ...state,
                uiProperties: payload.uiProperties,
                isOverlayDialogActive: payload.isOverlayDialogActive,
            };
        case SET_OVERLAY_DIALOG_TIMER_ID:
            return {
                ...state,
                timerId: payload.timerId,
            };
        default:
            return state;
    }
};

export const showOverlayDialog =
    (props: IOverlayDialog) => (dispatch: Function, getState: Function) => {
        dispatch(setOverlayDialogStateAction(props, true));
        dispatch(setOverlayHighlightVerticalPositionAction(0));
        dispatch(setOverlayHighlightVerticalLimitAction(props.options?.length));
        const { uiProperties } = getState().overlayDialog;
        if (!uiProperties.isPersistent) {
            const timerId = setTimeout(() => {
                dispatch(hideOverlayDialog());
                try {
                    uiProperties.onAutoHide();
                } catch (error) {
                    Log.e(TAG, 'onAutoHide is undefined', error);
                }
            }, OVERLAY_TIMEOUT);
            dispatch(setOverlayDialogTimerIdAction(timerId));
        }
    };

export const hideOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { timerId } = getState().overlayDialog;
    const { previousScreenTaskController } = getState().screens;
    const { isOverlayGhostHighlightActive } = getState().overlayHighlight;
    dispatch(setScreenTaskController(previousScreenTaskController));
    clearTimeout(timerId);
    dispatch(resetOverlayDialogState());
    dispatch(overlayDialogSetIsCaretVisibleAction(false));
    if (isOverlayGhostHighlightActive) dispatch(overlayDisableGhostMode());
};

export const resetOverlayDialogState = () => (dispatch: Function) => {
    dispatch(setOverlayDialogStateAction(defaultUIProperties, false));
};

export const onKeyDownOverlayDialaog = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, verticalLimit } = getState().overlayHighlight;
    const newVerticalPosition = verticalPosition + 1;
    if (newVerticalPosition < verticalLimit)
        dispatch(setOverlayHighlightVerticalPositionAction(newVerticalPosition));
};

export const onKeyLeftOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, verticalLimit, horizontalPosition, horizontalLimit } =
        getState().overlayHighlight;
    if (verticalPosition === verticalLimit - 1) {
        if (horizontalPosition > 0)
            dispatch(setOverlayHighlightHorizontalPositionAction(horizontalPosition - 1));
        else dispatch(setOverlayHighlightHorizontalPositionAction(horizontalLimit - 1));
    }
};

export const onKeyRightOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { horizontalPosition, horizontalLimit, verticalLimit, verticalPosition } =
        getState().overlayHighlight;
    if (horizontalPosition < horizontalLimit - 1 && verticalLimit - 1 === verticalPosition)
        dispatch(setOverlayHighlightHorizontalPositionAction(horizontalPosition + 1));
    else if (verticalLimit - 1 === verticalPosition)
        dispatch(setOverlayHighlightHorizontalPositionAction(0));
};

export const onKeyUpOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition } = getState().overlayHighlight;
    if (verticalPosition > 0)
        dispatch(setOverlayHighlightVerticalPositionAction(verticalPosition - 1));
};

export const onKeyEventResetOverlayDialogTimer = () => (dispatch: Function, getState: Function) => {
    const { timerId, isOverlayDialogActive, uiProperties } = getState().overlayDialog;
    clearTimeout(timerId);
    if (isOverlayDialogActive && !uiProperties.isPersistent) {
        const timer = setTimeout(() => {
            dispatch(hideOverlayDialog());
        }, OVERLAY_TIMEOUT);
        dispatch(setOverlayDialogTimerIdAction(timer));
    }
};

export const clearOverlayTaskController = () => (dispatch: Function, getState: Function) => {
    const { previousScreenTaskController } = getState().screens;
    dispatch(setScreenTaskController(previousScreenTaskController));
};

export const onMouseOutOverlayDialogOperations = () => (dispatch: Function, getState: Function) => {
    const { activeComponent } = getState().interactiveLayer;
    if (activeComponent.length > 0) dispatch(clearActiveInteractiveComponent());
    dispatch(overlayEnableGhostMode());
};

export const onMouseOverOverlayDialogOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        const { uiProperties } = getState().overlayDialog;
        if (value === uiProperties.mouseTrapValue) return;
        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(setActiveInteractiveComponent(value));
            return;
        }
        const overlaySetIsCaretVisible =
            uiProperties.enableCaret &&
            uiProperties.verticalPositionCaretAvailable?.indexOf(Number(value)) !== -1;
        dispatch(setOverlayHighlightVerticalPositionAction(Number(value)));
        dispatch(overlayDialogSetIsCaretVisibleAction(overlaySetIsCaretVisible));
        dispatch(overlayDisableGhostMode());
    };

export const overlayDialogSetCaretVisible = () => (dispatch: Function) => {
    dispatch(overlayDialogSetIsCaretVisibleAction(true));
};

export const overlayDialogSetCaretInvisible = () => (dispatch: Function, getState: Function) => {
    const { isCaretVisible } = getState().overlayDialog;
    if (isCaretVisible) dispatch(overlayDialogSetIsCaretVisibleAction(false));
};

export default overlayDialogReducer;
